import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import styled from "styled-components";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import { PrimaryButton, SecondaryButton, ButtonSizeOptions, InputText, InputTypeAhead } from "@loanbossinc/component-library";
import { updateDebtValuationSettingsForLoan, getDebtValuationSettingsByLoan, getDebtValuationConfigItems } from "pages/debtValuation/debtValuation.store";
import { BASE_INDEXES_FIXED, FLOATING_INDICES, SPREAD_TYPE, FIRST_PERIOD_ACCRUALS, DISCOUNT_METHOD } from "constants/configItemParents";

import { DiscountMethod, REPLACEMENT, FLOATINGDISCOUNT, SINGLE_SPREAD } from "../Calculators/Constants/DebtValuation";

const StyledDialogContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledContentRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 24px;
`;

const DebtValuationSettingsModal = ({ isOpen,
  handleClose,
  debtValuationSettings,
  configItems,
  loanDebtValuationSettings,
  getDebtValuationConfigItems,
  updateDebtValuationSettingsForLoan,
  getDebtValuationSettingsByLoan,
  refreshCashflows }) => {
  const [debtValuationSettingsData, setDebtValuationSettingsData] = useState({
    discountMethodId: null,
    baseIndexId: null,
    firstPeriodAccrualsId: null,
    spreadTypeId: null,
    calculationLTV: null,
    singleSpreadValue: null,
    isOverride: null
  });

  useEffect(() => {
    getDebtValuationConfigItems();
  }, []);
  useEffect(() => {
    if (debtValuationSettings && debtValuationSettings.loanId) {
      getDebtValuationSettingsByLoan({ loanId: debtValuationSettings.loanId });
    }
  }, [debtValuationSettings]);

  useEffect(() => {
    if (loanDebtValuationSettings && debtValuationSettings?.loanId && loanDebtValuationSettings[debtValuationSettings.loanId]) {
      const { discountMethodId,
        baseIndexId,
        firstPeriodAccrualsId,
        spreadTypeId,
        calculationLTV,
        singleSpreadValue,
        isOverride } = loanDebtValuationSettings[debtValuationSettings.loanId];
      setDebtValuationSettingsData({
        discountMethodId,
        baseIndexId,
        firstPeriodAccrualsId,
        spreadTypeId,
        calculationLTV,
        singleSpreadValue,
        isOverride
      });
    }
  }, [loanDebtValuationSettings]);

  const handleChange = (name, value) => {
    debtValuationSettingsData[name] = value;
    setDebtValuationSettingsData({ ...debtValuationSettingsData });
  };

  const handleSave = () => {
    handleClose();
    updateDebtValuationSettingsForLoan({
      loanId: debtValuationSettings.loanId,
      discountMethodId: debtValuationSettingsData.discountMethodId,
      baseIndexId: debtValuationSettingsData.baseIndexId,
      firstPeriodAccrualsId: debtValuationSettingsData.firstPeriodAccrualsId,
      spreadTypeId: debtValuationSettingsData.spreadTypeId,
      calculationLTV: debtValuationSettingsData.calculationLTV,
      singleSpreadValue: debtValuationSettingsData.singleSpreadValue,
      isOverride: debtValuationSettingsData.isOverride,
      callback: () => refreshCashflows()
    });
  };

  if (!debtValuationSettings ||
        !loanDebtValuationSettings ||
        (debtValuationSettings?.loanId && !loanDebtValuationSettings[debtValuationSettings.loanId])
  ) {
    return null;
  }

  return (
    <Dialog open={isOpen} style={{ height: "600px" }} maxWidth="md">
      <DialogTitle>Debt Valuation Settings</DialogTitle>
      <DialogContent>
        <StyledDialogContent>
          <StyledContentRow>
            <InputTypeAhead
              id={"discountMethodId"}
              name={"discountMethodId"}
              value={debtValuationSettingsData.discountMethodId}
              label="Discount Method"
              options={configItems.discountMethods}
              onChange={e => handleChange("discountMethodId", e)}
              displayWith={option => option && option.name}
              setValueWith={option => option && option.id}
              variant="filled"
              className="input"
              style={{ width: "220px", marginLeft: "5px", marginRight: "5px" }}
            />

            {debtValuationSettingsData.discountMethodId === DiscountMethod[REPLACEMENT].value && <InputTypeAhead
              id={"baseIndexId"}
              name={"baseIndexId"}
              value={debtValuationSettingsData.baseIndexId}
              label="Discount Base"
              options={configItems.fixedIndexes}
              onChange={e => handleChange("baseIndexId", e)}
              displayWith={option => option && option.name}
              setValueWith={option => option && option.id}
              variant="filled"
              className="input"
              style={{ width: "220px", marginLeft: "5px", marginRight: "5px" }}
            />}

            {debtValuationSettingsData.discountMethodId === DiscountMethod[FLOATINGDISCOUNT].value && <InputTypeAhead
              id={"baseIndexId"}
              name={"baseIndexId"}
              value={debtValuationSettingsData.baseIndexId}
              label="Discount Base"
              options={configItems.floatingIndexes}
              onChange={e => handleChange("baseIndexId", e)}
              displayWith={option => option && option.name}
              setValueWith={option => option && option.id}
              variant="filled"
              className="input"
              style={{ width: "220px", marginLeft: "5px", marginRight: "5px" }}
            />}

            <InputTypeAhead
              id={"firstPeriodAccrualsId"}
              name={"firstPeriodAccrualsId"}
              value={debtValuationSettingsData.firstPeriodAccrualsId}
              label="First Period Accruals"
              options={configItems.firstPeriodAccrualTypes}
              onChange={e => handleChange("firstPeriodAccrualsId", e)}
              displayWith={option => option && option.name}
              setValueWith={option => option && option.id}
              variant="filled"
              className="input"
              style={{ width: "220px", marginLeft: "5px", marginRight: "5px" }}
            />
          </StyledContentRow>

          <StyledContentRow>
            <InputTypeAhead
              id={"spreadTypeId"}
              name={"spreadTypeId"}
              value={debtValuationSettingsData.spreadTypeId}
              label="Spread Type"
              options={configItems.spreadTypes}
              onChange={e => handleChange("spreadTypeId", e)}
              displayWith={option => option && option.name}
              setValueWith={option => option && option.id}
              variant="filled"
              className="input"
              style={{ width: "220px", marginLeft: "5px", marginRight: "5px" }}
            />

            {debtValuationSettingsData.spreadTypeId && debtValuationSettingsData.spreadTypeId === SINGLE_SPREAD && <InputText
              id="singleSpreadValue"
              variant="filled"
              label="Calculation Spread (%)"
              name="singleSpreadValue"
              fullWidth
              onChange={({ target: { name, value } }) => handleChange(name, value)}
              value={debtValuationSettingsData.singleSpreadValue}
              style={{ width: "220px", marginLeft: "5px", marginRight: "5px" }}
            />}
          </StyledContentRow>
        </StyledDialogContent>
      </DialogContent>
      <DialogActions>
        <SecondaryButton
          label="Cancel"
          size={ButtonSizeOptions.sm}
          onClick={handleClose}
          style={{ marginTop: "16px", marginLeft: "5px", marginRight: "5px" }}
        />
        <PrimaryButton
          label="Save"
          size={ButtonSizeOptions.sm}
          onClick={handleSave}
          style={{ marginTop: "16px", marginLeft: "5px", marginRight: "5px" }}
        />

      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = ({ debtValuation }) => ({
  ...debtValuation,
  configItems: {
    discountMethods: debtValuation.configItems[DISCOUNT_METHOD],
    fixedIndexes: debtValuation.configItems[BASE_INDEXES_FIXED],
    firstPeriodAccrualTypes: debtValuation.configItems[FIRST_PERIOD_ACCRUALS],
    floatingIndexes: debtValuation.configItems[FLOATING_INDICES],
    spreadTypes: debtValuation.configItems[SPREAD_TYPE]
  }
});

export default compose(
  connect(mapStateToProps, {
    updateDebtValuationSettingsForLoan,
    getDebtValuationSettingsByLoan,
    getDebtValuationConfigItems
  })
)(DebtValuationSettingsModal);
